import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '@env';
import { ActiveStatusEnum } from '@models';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { firstValueFrom } from 'rxjs';

/**
 * Purpose-built multi-select picker for Program User Groups. Deliberately avoids the generic
 * EntityFinderDialog + ag-Grid stack because the ProgramUserGroup entity's self-referencing
 * ConsolidatedIntoPug navigation confuses Breeze's ref-resolver on payloads with consolidated
 * losers, which leaves ag-Grid's getRowId callback receiving corrupt row-id values (a raw
 * function reference instead of a Guid). That produced ghost blank rows that couldn't be
 * clicked without a NRE downstream.
 *
 * This picker reads a flat DTO list from AccountController.ProgramUserGroupPickerList via
 * plain HttpClient (no Breeze), renders a scrollable native checkbox list, and returns the
 * selected {Id, Name} pairs to the caller.
 *
 * Callers pass:
 *   - accountId          — which account's PUGs to load
 *   - excludePugIds      — PUGs already attached to this tree/scope; they're hidden from the list
 *   - includeOnlyPugIds  — optional allow-list; when set, every PUG outside it is hidden. Used by
 *                          the Account User form for scoped admins, who may only attach PUGs they
 *                          supervise. Omit it for unrestricted callers.
 *
 *   - allowShowInactive  — opt in to the "Show Inactive?" checkbox. Off by default: on the
 *                          Account User form the server rejects a ProgramUserGroupMap to an
 *                          Inactive PUG (BeforeSaveHandler.CheckProgramUserGroupMapAdds), so
 *                          listing one only offers a row that can never be saved. Approval
 *                          Trees has no such gate, so it turns the checkbox on.
 *   - allowSelectAll     — opt in to the "Select All" button. Off by default: attaching every
 *                          PUG in an account to one user is never a real intent, and one stray
 *                          click would create hundreds of maps. Bulk attach IS a real action on
 *                          an approval tree admin group, so that caller turns it on.
 *
 * Consolidated losers (IsConsolidated=true) are shown grayed out with a "consolidated" tag but
 * are not selectable. Hold and Phaseout PUGs always stay in the list and keep their status tag,
 * because both are valid states to attach to.
 */
export interface PugPickerParams {
  accountId: string;
  excludePugIds?: string[];
  includeOnlyPugIds?: string[];
  allowShowInactive?: boolean;
  allowSelectAll?: boolean;
}

interface PugPickerRow {
  Id: string;
  Name: string;
  ActiveStatusId: ActiveStatusEnum;
  IsConsolidated: boolean;
  selected: boolean;
}

@Component({
  selector: 'prox-program-user-group-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    ProgressSpinnerModule,
    TagModule,
  ],
  template: `
    <div class="picker-frame flex h-full flex-col">
      <div class="mb-2 flex flex-wrap items-center gap-3">
        <input
          pInputText
          type="text"
          [(ngModel)]="filterText"
          placeholder="Filter by name..."
          class="filter-input"
          [disabled]="loading"
        />
        @if (allowShowInactive) {
          <div class="flex items-center gap-2">
            <p-checkbox [(ngModel)]="showInactive" [binary]="true" inputId="showInactive" />
            <label for="showInactive" class="text-sm">Show Inactive?</label>
          </div>
        }
        @if (allowSelectAll) {
          <p-button
            label="Select All"
            size="small"
            severity="secondary"
            [disabled]="loading || visibleRows.length === 0"
            (onClick)="selectAllVisible()"
          />
        }
        <p-button
          label="Clear"
          size="small"
          severity="secondary"
          [disabled]="loading || selectedCount === 0"
          (onClick)="clearAll()"
        />
        <div class="ml-auto text-sm text-gray-600">
          {{ visibleRows.length }} of {{ allRows.length }} shown &mdash;
          <b>{{ selectedCount }}</b> selected
        </div>
      </div>

      @if (loading) {
        <div class="flex items-center justify-center py-8">
          <p-progressSpinner styleClass="h-10 w-10" />
        </div>
      } @else if (loadError) {
        <div class="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          <div class="font-semibold">Error loading Program User Groups</div>
          <div>{{ loadError }}</div>
        </div>
      } @else {
        <div class="row-scroll">
          @for (row of visibleRows; track row.Id) {
            <label
              class="row"
              [class.row-selected]="row.selected"
              [class.row-disabled]="row.IsConsolidated"
            >
              <input
                type="checkbox"
                [(ngModel)]="row.selected"
                [disabled]="row.IsConsolidated"
                class="row-check"
              />
              <span class="row-name">{{ row.Name }}</span>
              @if (row.ActiveStatusId !== ActiveStatusEnum.Active) {
                <p-tag
                  [severity]="row.ActiveStatusId === ActiveStatusEnum.Hold ? 'warn' : 'secondary'"
                  [value]="statusLabel(row.ActiveStatusId)"
                />
              }
              @if (row.IsConsolidated) {
                <span class="text-xs italic text-gray-500">(consolidated)</span>
              }
            </label>
          }
          @if (visibleRows.length === 0) {
            <div class="py-4 text-center text-gray-500">No Program User Groups match the current filter.</div>
          }
        </div>
      }

      @if (!loading && !loadError) {
        <div class="mt-3 flex justify-start gap-2">
          <p-button
            label="Ok"
            icon="pi pi-check"
            severity="primary"
            size="small"
            [disabled]="selectedCount === 0"
            (onClick)="onOk()"
          />
          <p-button label="Cancel" icon="pi pi-times" severity="secondary" size="small" (onClick)="onCancel()" />
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        padding: 1rem 1rem 0.25rem 1rem;
        min-width: 40rem;
      }
      .picker-frame {
        min-height: 48rem;
      }
      .filter-input {
        width: 22rem;
      }
      .row-scroll {
        flex: 1 1 0;
        min-height: 32rem;
        max-height: 64rem;
        overflow-y: auto;
        border: 1px solid var(--surface-border);
        border-radius: 6px;
      }
      .row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.35rem 0.75rem;
        border-top: 1px solid var(--surface-border);
        cursor: pointer;
        font-size: 0.9rem;
      }
      .row:first-child {
        border-top: none;
      }
      .row:hover {
        background: var(--surface-50);
      }
      .row-selected {
        background: #e3f2fd;
      }
      .row-selected:hover {
        background: #d0e8fa;
      }
      .row-disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }
      .row-check {
        width: 1rem;
        height: 1rem;
      }
      .row-name {
        flex: 1;
      }
    `,
  ],
})
export class ProgramUserGroupPickerDialogComponent implements OnInit {
  private http = inject(HttpClient);
  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig<PugPickerParams>);

  allRows: PugPickerRow[] = [];
  filterText = '';
  showInactive = false;
  allowShowInactive = false;
  allowSelectAll = false;
  loading = false;
  loadError: string | null = null;

  ActiveStatusEnum = ActiveStatusEnum;

  ngOnInit(): void {
    const params = this.config.data;
    if (!params?.accountId) {
      this.loadError = 'No accountId supplied to picker.';
      return;
    }
    // Explicit <string>: DynamicDialogConfig.data is typed `any`, so an unannotated
    // `new Set(...)` here infers Set<unknown> and fails to match load()'s parameter.
    const includeOnly = params.includeOnlyPugIds ? new Set<string>(params.includeOnlyPugIds) : null;
    this.allowShowInactive = params.allowShowInactive === true;
    this.allowSelectAll = params.allowSelectAll === true;
    this.load(params.accountId, new Set(params.excludePugIds ?? []), includeOnly);
  }

  private async load(accountId: string, exclude: Set<string>, includeOnly: Set<string> | null) {
    this.loading = true;
    try {
      const params = new HttpParams().set('accountId', accountId);
      const rows = await firstValueFrom(
        this.http.get<PugPickerRow[]>(`${environment.accountUrl}/ProgramUserGroupPickerList`, { params }),
      );
      this.allRows = rows
        // When the caller does not offer the checkbox, Inactive PUGs are dropped here rather
        // than in visibleRows, so the "x of y shown" counter reports only attachable PUGs.
        .filter((r) => this.allowShowInactive || r.ActiveStatusId !== ActiveStatusEnum.Inactive)
        .filter((r) => !exclude.has(r.Id))
        .filter((r) => includeOnly == null || includeOnly.has(r.Id))
        .map((r) => ({ ...r, selected: false }));
    } catch (err: any) {
      this.loadError = err?.error?.Message ?? err?.message ?? String(err);
    } finally {
      this.loading = false;
    }
  }

  get visibleRows(): PugPickerRow[] {
    const filter = this.filterText.trim().toLowerCase();
    return this.allRows.filter((r) => {
      if (!this.showInactive && r.ActiveStatusId === ActiveStatusEnum.Inactive) return false;
      return !filter || r.Name.toLowerCase().includes(filter);
    });
  }

  get selectedCount(): number {
    return this.allRows.filter((r) => r.selected).length;
  }

  selectAllVisible(): void {
    for (const r of this.visibleRows) {
      if (!r.IsConsolidated) r.selected = true;
    }
  }

  clearAll(): void {
    for (const r of this.allRows) r.selected = false;
  }

  statusLabel(id: ActiveStatusEnum): string {
    return ActiveStatusEnum[id];
  }

  onOk(): void {
    const picked = this.allRows.filter((r) => r.selected).map((r) => ({ Id: r.Id, Name: r.Name }));
    this.ref.close(picked);
  }

  onCancel(): void {
    this.ref.close([]);
  }
}
